// The twenty monster bodies of mockup round 109 (R2719, v2-the-deep.md §8.4,
// ui-map-interface.md §6.1.15): five kinds, four designs each, with the
// quill pod of the Quillback, its trail and its burst.
//
// **The port.** The card page draws each body on a 2D canvas: filled
// polygons, tubes round a spine, ellipses, strokes and gradients. The map has
// no filled path, so each body is written here against a PEN of capsules
// and circles (`MonsterPen`). A filled part is a SHAPE: a union of capsules
// that the kit draws twice, first wide and then at its width, so the union
// reads as one outlined body, as `ink()` of the page does. The page puts
// the deep edge round the NPC ink; the map draws the INVERSE HULL, the NPC
// ink round the deep body (R2874). A tube is a capsule a spine step; an
// ellipse is a stadium along its long axis; a polygon is a fan of capsules
// from its centre. An eye, a tooth and a lamp are discs, which keep their
// place in the draw order; a ring, an arc and a dashed ring stand on the
// body.
//
// **The figures are the drawing.** The spine lengths, the wave clocks and
// the angles are the page's own literals (`archive/mockups/
// 109-monster-marks.html`), in its units: one unit is one map px at a mark
// scale of 1, and the bow points east. Each body names its card.
//
// **The state** (`MonsterState`) is the `s` of the page: what the body does
// now (swim, open, bite, hide, reach, swell, charge, pulse, gather). The
// layer writes it from the act on the wire (`actPose`), and the body reads
// it. Nothing here allocates on the frame path: every buffer is a module
// array, reused.
//
// This is a leaf (`render/leaves.test.ts`).

import { out as eout } from './combat-recipe-kit';
import { TAU, clamp01 } from './math';
import { ease, lerp } from './recipe-kit';
import { mulberry32 } from '../world';

/** The NPC ink, and the deep edge ink of the NPC side (`--npc`, `--npc-edge`). */
export const LIGHT = 0;
export const DARK = 1;
/** One of the two inks of the NPC side. */
export type MonsterInk = typeof LIGHT | typeof DARK;

const PI = Math.PI;

/**
 * What a body draws with. Every figure is in the body's local units, bow
 * east; the pen turns, scales and places them. `ew` is the width of the
 * dark edge in local units: the page widens it as the view zooms out
 * (`ew = zoom^-0.45`), so the edge never thins to nothing.
 */
export interface MonsterPen {
  ew: number;
  /** A capsule from one point to the other, `w` wide. */
  cap(x0: number, y0: number, x1: number, y1: number, w: number, ink: MonsterInk, a: number): void;
  disc(x: number, y: number, r: number, ink: MonsterInk, a: number): void;
  ring(x: number, y: number, r: number, w: number, ink: MonsterInk, a: number): void;
  arc(x: number, y: number, r: number, w: number, start: number, sweep: number, ink: MonsterInk, a: number): void;
  dashed(x: number, y: number, r: number, w: number, count: number, fill: number, ink: MonsterInk, a: number): void;
  /** A soft glow in the NPC ink, `a` at the centre and nothing at `r`. */
  glow(x: number, y: number, r: number, a: number): void;
  /** Places, turns and scales what follows, until `pop`. */
  push(x: number, y: number, rot: number, scale: number): void;
  pop(): void;
}

/**
 * A pen that keeps a local transform and hands world figures to `emit*`.
 * The batch pen of the map and the SVG pen of the legend extend it, so both
 * draw one body the same way.
 */
export abstract class TransformPen implements MonsterPen {
  ew = 1;
  protected ox = 0;
  protected oy = 0;
  protected ca = 1;
  protected sa = 0;
  protected k = 1;
  /** The saved transforms of `push`: five numbers a level. */
  private readonly stack = new Float64Array(5 * 4);
  private depth = 0;

  /** Sets the base transform: the body's place, bow and scale. */
  place(x: number, y: number, rot: number, scale: number): void {
    this.ox = x; this.oy = y;
    this.ca = Math.cos(rot); this.sa = Math.sin(rot);
    this.k = scale;
    this.depth = 0;
  }

  push(x: number, y: number, rot: number, scale: number): void {
    if (this.depth < 4) {
      const o = this.depth * 5, st = this.stack;
      st[o] = this.ox; st[o + 1] = this.oy; st[o + 2] = this.ca; st[o + 3] = this.sa; st[o + 4] = this.k;
    }
    this.depth++;
    const nx = this.wx(x, y), ny = this.wy(x, y);
    const c = Math.cos(rot), s = Math.sin(rot);
    const ca = this.ca * c - this.sa * s, sa = this.sa * c + this.ca * s;
    this.ox = nx; this.oy = ny; this.ca = ca; this.sa = sa; this.k *= scale;
  }

  pop(): void {
    if (this.depth === 0) return;
    this.depth--;
    if (this.depth >= 4) return;
    const o = this.depth * 5, st = this.stack;
    this.ox = st[o]; this.oy = st[o + 1]; this.ca = st[o + 2]; this.sa = st[o + 3]; this.k = st[o + 4];
  }

  protected wx(x: number, y: number): number { return this.ox + (this.ca * x - this.sa * y) * this.k; }
  protected wy(x: number, y: number): number { return this.oy + (this.sa * x + this.ca * y) * this.k; }
  /** The turn of the local frame, for an arc's start angle. */
  protected get turn(): number { return Math.atan2(this.sa, this.ca); }

  cap(x0: number, y0: number, x1: number, y1: number, w: number, ink: MonsterInk, a: number): void {
    if (a <= 0.004) return;
    this.emitCap(this.wx(x0, y0), this.wy(x0, y0), this.wx(x1, y1), this.wy(x1, y1), w * this.k, ink, a);
  }

  disc(x: number, y: number, r: number, ink: MonsterInk, a: number): void {
    if (a <= 0.004 || r <= 0) return;
    this.emitDisc(this.wx(x, y), this.wy(x, y), r * this.k, ink, a);
  }

  ring(x: number, y: number, r: number, w: number, ink: MonsterInk, a: number): void {
    if (a <= 0.004 || r <= 0) return;
    this.emitRing(this.wx(x, y), this.wy(x, y), r * this.k, w * this.k, ink, a);
  }

  arc(x: number, y: number, r: number, w: number, start: number, sweep: number, ink: MonsterInk, a: number): void {
    if (a <= 0.004 || r <= 0) return;
    this.emitArc(this.wx(x, y), this.wy(x, y), r * this.k, w * this.k, start + this.turn, sweep, ink, a);
  }

  dashed(x: number, y: number, r: number, w: number, count: number, fill: number, ink: MonsterInk, a: number): void {
    if (a <= 0.004 || r <= 0) return;
    this.emitDashed(this.wx(x, y), this.wy(x, y), r * this.k, w * this.k, count, fill, this.turn, ink, a);
  }

  glow(x: number, y: number, r: number, a: number): void {
    if (a <= 0.004 || r <= 0) return;
    this.emitGlow(this.wx(x, y), this.wy(x, y), r * this.k, a);
  }

  protected abstract emitCap(x0: number, y0: number, x1: number, y1: number, w: number, ink: MonsterInk, a: number): void;
  protected abstract emitDisc(x: number, y: number, r: number, ink: MonsterInk, a: number): void;
  protected abstract emitRing(x: number, y: number, r: number, w: number, ink: MonsterInk, a: number): void;
  protected abstract emitArc(x: number, y: number, r: number, w: number, start: number, sweep: number, ink: MonsterInk, a: number): void;
  protected abstract emitDashed(x: number, y: number, r: number, w: number, count: number, fill: number, start: number, ink: MonsterInk, a: number): void;
  protected abstract emitGlow(x: number, y: number, r: number, a: number): void;
}

// ---------------------------------------------------------------------------
// The state of a body
// ---------------------------------------------------------------------------

/** What a body does now: the `s` of the card page. */
export interface MonsterState {
  /** The body's own clock, in seconds. */
  t: number;
  swim: number;
  open: number;
  bite: number;
  hide: number;
  reach: number;
  grab: number;
  ink: number;
  swell: number;
  spent: number;
  regrow: number;
  charge: number;
  /** The share of a discharge, 0 to 1, or -1 when none runs. */
  pulse: number;
  gather: number;
  tell: number;
  gulp: number;
  /** The target in local units, and whether there is one. */
  tx: number;
  ty: number;
  hasTarget: boolean;
  /** The arcs of a discharge in local units, as x, y pairs, and their
   *  count: every hull in reach (R2719). With none, the arc goes to the
   *  target. The array is made once with the state. */
  zaps: Float32Array;
  nZaps: number;
  /** The share of the bodies a Myriad keeps (ruling R-e), 0 to 1. */
  bodies: number;
  alpha: number;
}

/** A state at rest: the body holds and does nothing. */
export function newMonsterState(): MonsterState {
  const s = {} as MonsterState;
  s.zaps = new Float32Array(2 * ZAP_MAX);
  restState(s);
  return s;
}

/** Puts a state back to rest. The clock and the alpha stay. */
export function restState(s: MonsterState): void {
  s.t = s.t ?? 0;
  s.alpha = s.alpha ?? 1;
  s.swim = 0; s.open = 0; s.bite = 0; s.hide = 0; s.reach = 0; s.grab = 0;
  s.ink = 0; s.swell = 0; s.spent = 0; s.regrow = 1; s.charge = 0; s.pulse = -1;
  s.gather = 0; s.tell = 0; s.gulp = 0; s.tx = 22; s.ty = 0; s.hasTarget = false;
  s.bodies = 1; s.nZaps = 0;
}

// ---------------------------------------------------------------------------
// Small maths, as the page states it
// ---------------------------------------------------------------------------

const win = (k: number, a: number, b: number): number => clamp01((k - a) / (b - a));
const pulseAt = (k: number, at: number, len: number): number => (k < at || k > at + len) ? 0 : 1 - (k - at) / len;
function lerpAng(a: number, b: number, u: number): number {
  const d = ((b - a + PI) % TAU + TAU) % TAU - PI;
  return a + d * u;
}
/** A hash of two integers to 0..1: the reseeded sparks of a charge. It
 *  stands in for the page's `mulberry32(seed)`, which allocates. */
function hash(a: number, b: number): number {
  let h = Math.imul(a | 0, 0x27d4eb2d) ^ Math.imul(b | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

// ---------------------------------------------------------------------------
// The kit: shapes of capsules
// ---------------------------------------------------------------------------

/** The capsules of the shape being built: x0, y0, x1, y1, w. */
const SHAPE = new Float32Array(5 * 384);
let nShape = 0;

function sc(x0: number, y0: number, x1: number, y1: number, w: number): void {
  if (nShape >= 384) return;
  const o = nShape * 5;
  SHAPE[o] = x0; SHAPE[o + 1] = y0; SHAPE[o + 2] = x1; SHAPE[o + 3] = y1; SHAPE[o + 4] = w;
  nShape++;
}

/**
 * Draws the shape as the INVERSE HULL (R2874, mockup round 134 card 01):
 * the rim pass in the NPC ink, then the body pass in the deep ink. The
 * page draws the two the other way round (`ink()`). Every other part of a
 * body keeps the ink the page gives it: an eye, a lamp, a tooth and an
 * effect in the water.
 */
function shape(p: MonsterPen, s: MonsterState, a = 1, ewK = 1): void {
  const e = 2 * p.ew * ewK, al = s.alpha;
  const da = 0.9 * Math.min(1, a / 0.9) * al;
  for (let i = 0; i < nShape; i++) {
    const o = i * 5;
    p.cap(SHAPE[o], SHAPE[o + 1], SHAPE[o + 2], SHAPE[o + 3], SHAPE[o + 4] + e, LIGHT, da);
  }
  for (let i = 0; i < nShape; i++) {
    const o = i * 5;
    p.cap(SHAPE[o], SHAPE[o + 1], SHAPE[o + 2], SHAPE[o + 3], SHAPE[o + 4], DARK, a * al);
  }
  nShape = 0;
}

/** Draws the shape in the deep ink alone (`dk()`). */
function shapeDark(p: MonsterPen, s: MonsterState, a = 0.9): void {
  for (let i = 0; i < nShape; i++) {
    const o = i * 5;
    p.cap(SHAPE[o], SHAPE[o + 1], SHAPE[o + 2], SHAPE[o + 3], SHAPE[o + 4], DARK, a * s.alpha);
  }
  nShape = 0;
}

/** A filled round part. */
function sBlob(x: number, y: number, r: number): void { sc(x, y, x, y, 2 * r); }

/** A filled ellipse, as the stadium along its long axis. */
function sEll(x: number, y: number, rx: number, ry: number, rot = 0): void {
  const ax = Math.max(0.01, rx), ay = Math.max(0.01, ry);
  const long = Math.max(ax, ay), short = Math.min(ax, ay), h = long - short;
  const a = ax >= ay ? rot : rot + PI / 2;
  const dx = Math.cos(a) * h, dy = Math.sin(a) * h;
  sc(x - dx, y - dy, x + dx, y + dy, 2 * short);
}

/** A quadratic run of `steps` capsules. */
function sQuad(x0: number, y0: number, cx: number, cy: number, x1: number, y1: number, w: number, steps = 4): void {
  let ax = x0, ay = y0;
  for (let k = 1; k <= steps; k++) {
    const u = k / steps, v = 1 - u;
    const bx = v * v * x0 + 2 * v * u * cx + u * u * x1, by = v * v * y0 + 2 * v * u * cy + u * u * y1;
    sc(ax, ay, bx, by, w);
    ax = bx; ay = by;
  }
}

/** The page's open curve through three points (`smoothOpen`). */
function sCurve3(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number, w: number): void {
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  sQuad(x0, y0, x1, y1, mx, my, w, 3);
  sc(mx, my, x2, y2, w);
}

/**
 * A tube round a spine of `n` points of `pts` (`tube()`): the half width is
 * `a * (1 - b u)^pw + c`, plus a bump of `bump` at `at` (the siphon's bulge).
 */
function sTube(
  pts: Float32Array, n: number, a: number, b: number, pw: number, c: number, bump = 0, at = 0,
): void {
  for (let i = 0; i < n - 1; i++) {
    const u = (i + 0.5) / (n - 1);
    const base = 1 - b * u;
    let hw = a * Math.pow(Math.max(0, base), pw) + c;
    if (bump !== 0) hw += bump * Math.exp(-((u - at) * (u - at)) / 0.01);
    sc(pts[i * 2], pts[i * 2 + 1], pts[i * 2 + 2], pts[i * 2 + 3], 2 * Math.max(0.2, hw));
  }
}

/**
 * A filled polygon of `n` points: a fan of capsules from its centre to each
 * corner and to each side, each pulled in by half its width so it does not
 * stand out past the side, and the sides themselves as thin capsules.
 */
function sPoly(pts: ArrayLike<number>, n: number): void {
  let cx = 0, cy = 0;
  for (let i = 0; i < n; i++) { cx += pts[i * 2]; cy += pts[i * 2 + 1]; }
  cx /= n; cy /= n;
  for (let i = 0; i < n; i++) {
    const x0 = pts[i * 2], y0 = pts[i * 2 + 1];
    const j = (i + 1) % n, x1 = pts[j * 2], y1 = pts[j * 2 + 1];
    sc(x0, y0, x1, y1, 0.6);
    // A spoke to the corner, and one to the middle of the side.
    spoke(cx, cy, x0, y0, 0.9);
    const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
    const ex = x1 - x0, ey = y1 - y0, el = Math.hypot(ex, ey) || 1;
    const d = Math.abs((cx - x0) * ey - (cy - y0) * ex) / el;
    spoke(cx, cy, mx, my, Math.max(0.6, Math.min(el * 0.55, 2 * d)));
  }
}

function spoke(cx: number, cy: number, x: number, y: number, w: number): void {
  const dx = x - cx, dy = y - cy, l = Math.hypot(dx, dy);
  if (l < 1e-6) { sc(cx, cy, cx, cy, w); return; }
  const back = Math.min(l, w / 2) / l;
  sc(cx, cy, x - dx * back, y - dy * back, w);
}

/** A light stroke and nothing under it (`strokeNpc`). */
function strokeL(p: MonsterPen, s: MonsterState, x0: number, y0: number, x1: number, y1: number, w: number, a = 1): void {
  p.cap(x0, y0, x1, y1, w, LIGHT, a * s.alpha);
}

/** A dark stroke (`strokeDk`). */
function strokeD(p: MonsterPen, s: MonsterState, x0: number, y0: number, x1: number, y1: number, w: number, a = 0.9): void {
  p.cap(x0, y0, x1, y1, w, DARK, a * s.alpha);
}

/** A light quadratic stroke. */
function quadL(p: MonsterPen, s: MonsterState, x0: number, y0: number, cx: number, cy: number, x1: number, y1: number, w: number, a = 1, steps = 4): void {
  let ax = x0, ay = y0;
  for (let k = 1; k <= steps; k++) {
    const u = k / steps, v = 1 - u;
    const bx = v * v * x0 + 2 * v * u * cx + u * u * x1, by = v * v * y0 + 2 * v * u * cy + u * u * y1;
    strokeL(p, s, ax, ay, bx, by, w, a);
    ax = bx; ay = by;
  }
}

/** A dark quadratic stroke. */
function quadD(p: MonsterPen, s: MonsterState, x0: number, y0: number, cx: number, cy: number, x1: number, y1: number, w: number, a = 0.9, steps = 3): void {
  let ax = x0, ay = y0;
  for (let k = 1; k <= steps; k++) {
    const u = k / steps, v = 1 - u;
    const bx = v * v * x0 + 2 * v * u * cx + u * u * x1, by = v * v * y0 + 2 * v * u * cy + u * u * y1;
    strokeD(p, s, ax, ay, bx, by, w, a);
    ax = bx; ay = by;
  }
}

/** A limb through three points: a light stroke over a dark edge (`limb`). */
function limb3(p: MonsterPen, s: MonsterState, x0: number, y0: number, x1: number, y1: number, x2: number, y2: number, w: number, a = 1): void {
  sCurve3(x0, y0, x1, y1, x2, y2, w);
  shape(p, s, a);
}

/** A straight limb. */
function limb2(p: MonsterPen, s: MonsterState, x0: number, y0: number, x1: number, y1: number, w: number, a = 1): void {
  sc(x0, y0, x1, y1, w);
  shape(p, s, a);
}

/** Two dark eye dots. */
function eyes(p: MonsterPen, s: MonsterState, x: number, y: number, r: number): void {
  p.disc(x, -y, r, DARK, 0.9 * s.alpha);
  p.disc(x, y, r, DARK, 0.9 * s.alpha);
}

/** A tail fin of the page (`[[0,0],[w,-h],[w*0.74,0],[w,h]]`) at a spine end. */
function sFin(x: number, y: number, a: number, w: number, h: number, cw = 1.2): void {
  const c = Math.cos(a), sn = Math.sin(a);
  sc(x, y, x + c * w + sn * h, y + sn * w - c * h, cw);
  sc(x, y, x + c * w - sn * h, y + sn * w + c * h, cw);
}

/** The spine buffer: up to 32 points. */
const SP = new Float32Array(64);
/** A second point buffer for polygons. */
const PA = new Float32Array(32);
const PB = new Float32Array(32);
const PC = new Float32Array(32);

/** Writes two points into `buf` at `o`. */
function put(buf: Float32Array, o: number, x0: number, y0: number, x1: number, y1: number): void {
  buf[o] = x0; buf[o + 1] = y0; buf[o + 2] = x1; buf[o + 3] = y1;
}

/** A spine from x0 (the head) to x1 (the tail) with a wave that grows to the
 *  tail (`wave()`), into `out`. */
function wave(out: Float32Array, x0: number, x1: number, n: number, amp: number, kk: number, ph: number, pw: number): void {
  for (let i = 0; i < n; i++) {
    const u = i / (n - 1);
    out[i * 2] = x0 + (x1 - x0) * u;
    out[i * 2 + 1] = amp * Math.sin(i * kk - ph) * Math.pow(u, pw);
  }
}

/** Turns `n` points of `src` about (cx, cy) by `a`, into `out`. */
function rotInto(src: ArrayLike<number>, n: number, cx: number, cy: number, a: number, out: Float32Array): void {
  const c = Math.cos(a), sn = Math.sin(a);
  for (let i = 0; i < n; i++) {
    const x = src[i * 2] - cx, y = src[i * 2 + 1] - cy;
    out[i * 2] = cx + x * c - y * sn;
    out[i * 2 + 1] = cy + x * sn + y * c;
  }
}

/** A ring that opens to 70 units (`ringPulse`): the discharge of a Thrummer. */
function ringPulse(p: MonsterPen, s: MonsterState, pu: number, w: number): void {
  const R = Math.max(0.1, 70 * eout(pu));
  p.disc(0, 0, R, LIGHT, 0.05 * (1 - pu) * s.alpha);
  p.ring(0, 0, R, w * (1 - pu) + 0.5, LIGHT, 0.8 * (1 - pu) * s.alpha);
}

/** A jagged line of `n` steps (`zig`), as light capsules. */
function zig(p: MonsterPen, s: MonsterState, x0: number, y0: number, x1: number, y1: number, seed: number, amp: number, n: number, w: number, a: number): void {
  const dx = x1 - x0, dy = y1 - y0, l = Math.hypot(dx, dy) || 1, nx = -dy / l, ny = dx / l;
  let ax = x0, ay = y0;
  for (let i = 1; i <= n; i++) {
    const u = i / n, j = i === n ? 0 : (hash(seed, i) - 0.5) * 2 * amp;
    const bx = x0 + dx * u + nx * j, by = y0 + dy * u + ny * j;
    strokeL(p, s, ax, ay, bx, by, w, a);
    ax = bx; ay = by;
  }
}

/** The most arcs one discharge draws. */
const ZAP_MAX = 8;

/** The arcs to the hulls inside 72 units while a discharge is young
 *  (`zapHulls`): one to each hull of `zaps`, else one to the target. The
 *  map fills `zaps` with every hull of a faction in reach (R2719). */
function zap(p: MonsterPen, s: MonsterState, pu: number): void {
  if (!(pu >= 0 && pu < 0.4)) return;
  const seed = Math.floor(s.t * 20), a = 1 - pu / 0.4;
  if (s.nZaps > 0) {
    for (let i = 0; i < s.nZaps; i++) {
      const x = s.zaps[2 * i], y = s.zaps[2 * i + 1];
      if (Math.hypot(x, y) <= 72) zig(p, s, 0, 0, x, y, seed + 7 * i, 3, 7, 0.9, a);
    }
    return;
  }
  if (!s.hasTarget || Math.hypot(s.tx, s.ty) > 72) return;
  zig(p, s, 0, 0, s.tx, s.ty, seed, 3, 7, 0.9, a);
}

// ===========================================================================
// A. THE GULPER, the contact ram (cards 01 to 04)
// ===========================================================================

const UP_JAW = [1.2, -5.5, 8.5, -4.9, 13, -1.2, 13, -0.1, 3.5, 0.6];
const LO_JAW = [3.5, 0.6, 12.6, 0.7, 11, 3.0, 5, 5.4, 1.5, 4.8];

/** Card 01, Hinged Jaw: a heavy head, a lower jaw on a hinge, a long tail. */
function g01(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, ph = t * (2.4 + 5.5 * sw), amp = 0.6 + 2.4 * sw;
  wave(SP, 3, -12, 9, amp, 0.8, ph, 1.4);
  sTube(SP, 9, 5.4, 1, 1.1, 0.4);
  const ex = SP[16], ey = SP[17], ta = Math.atan2(ey - SP[15], ex - SP[14]);
  sFin(ex, ey, ta, 3.8, 3.2, 1.4);
  shape(p, s);
  const a = clamp01(s.open) * 0.95 + 0.05 * Math.sin(t * 1.7) * (1 - sw), hx = 3.5, hy = 0.6;
  rotInto(UP_JAW, 5, hx, hy, -a * 0.3, PA);
  rotInto(LO_JAW, 5, hx, hy, a, PB);
  if (a > 0.04) {
    put(PC, 0, hx, hy, PA[6], PA[7]); PC[4] = PB[2]; PC[5] = PB[3];
    sPoly(PC, 3);
    shapeDark(p, s, 0.95);
    for (let j = 1; j <= 3; j++) {
      const u = j / 4;
      const px = lerp(PA[8], PA[6], u), py = lerp(PA[9], PA[7], u);
      const qx = lerp(PB[0], PB[2], u), qy = lerp(PB[1], PB[3], u);
      strokeL(p, s, px, py, lerp(px, qx, 0.35), lerp(py, qy, 0.35), 0.9);
      strokeL(p, s, qx, qy, lerp(qx, px, 0.35), lerp(qy, py, 0.35), 0.9);
    }
  }
  sPoly(PB, 5); shape(p, s);
  sPoly(PA, 5); shape(p, s);
  const c = Math.cos(-a * 0.3), sn = Math.sin(-a * 0.3);
  const exx = hx + (7.5 - hx) * c - (-2.6 - hy) * sn, eyy = hy + (7.5 - hx) * sn + (-2.6 - hy) * c;
  p.disc(exx, eyy, 1.15, DARK, 0.9 * s.alpha);
  p.disc(exx + 0.3, eyy - 0.3, 0.35, LIGHT, s.alpha);
}

/** Card 02, Ring Maw: the ragged ring of R2637 grown into a body. */
function g02(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, o = clamp01(s.open), b = s.bite;
  p.push(0, 0, 0, 26 / 24);
  for (let i = 0; i < 6; i++) {
    const a0 = i * PI / 3 + 0.3;
    let ta = a0 + 0.2 + 0.18 * Math.sin(t * 1.3 + i);
    ta = lerpAng(ta, PI + (i - 2.5) * 0.26, sw * (1 - o));
    ta = lerpAng(ta, (i - 2.5) * 0.3, o);
    const ba = lerpAng(a0, ta, 0.35 * Math.max(sw, o)), tr = 10.5 + o * 4 + sw * 2 - b * 5;
    const ca = lerpAng(ba, ta, 0.5) + 0.4 * Math.sin(t * (2 + 4 * sw) + i) * (0.4 + 0.6 * sw);
    sQuad(Math.cos(ba) * 5, Math.sin(ba) * 5, Math.cos(ca) * 8.5, Math.sin(ca) * 8.5,
      Math.cos(ta) * tr, Math.sin(ta) * tr, 1.4, 4);
    shape(p, s, 1, 0.9);
  }
  const k = 1 + 0.12 * o - 0.08 * Math.sin(t * 6) * sw, rr = t * 0.35;
  sBlob(0, 0, 5.2 * k); shape(p, s, 0.95);
  p.ring(0, 0, 4.6 * k, 1.1, DARK, 0.9 * s.alpha);
  p.dashed(0, 0, 5.4 * k, 0.7, 12, 0.5, DARK, 0.9 * s.alpha);
  const rm = (1.4 + 2.6 * o) * k;
  p.disc(0, 0, rm, DARK, 0.95 * s.alpha);
  if (o > 0.15) {
    for (let j = 0; j < 8; j++) {
      const a = j * PI / 4 + rr, r = rm - 0.5 * o;
      p.disc(Math.cos(a) * r, Math.sin(a) * r, 0.3 + 0.25 * o, LIGHT, s.alpha);
    }
  }
  p.pop();
}

/** Card 03, Ram Plate: an armoured head plate on four body plates. */
function g03(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, o = clamp01(s.open), b = s.bite;
  const gap = 1 + 0.12 * Math.sin(t * 1.5) * (1 - sw) - 0.35 * b, ph = t * (1.6 + 4 * sw), amp = 0.4 + 1.4 * sw;
  const xt = 2 - 5 * 3.3 * gap, yt = amp * Math.sin(5 * 0.9 - ph);
  sc(xt + 1.5, yt, xt - 3.5, yt - 4, 1.3); sc(xt + 1.5, yt, xt - 3.5, yt + 4, 1.3);
  shape(p, s);
  for (let i = 4; i >= 1; i--) {
    const x = 2 - i * 3.3 * gap, y = amp * Math.sin(i * 0.9 - ph);
    sEll(x, y, 2.6, 6.3 - i * 0.95); shape(p, s);
  }
  for (let sd = -1; sd <= 1; sd += 2) {
    const tx = lerp(17 + Math.sin(t * 2 + sd), 9.5, o), ty = sd * lerp(4 + 1.5 * Math.sin(t * 2.2 + sd), 7.5, o);
    quadL(p, s, 11.5, sd * 1.8, 14, sd * 2.4, tx, ty, 0.7, 0.85, 3);
  }
  put(PC, 0, 13 + o * 1.5, 0, 8, -6.4 - o);
  put(PC, 4, 2.5, -6.8 - o * 0.6, 3.5, 0);
  put(PC, 8, 2.5, 6.8 + o * 0.6, 8, 6.4 + o);
  sPoly(PC, 6); shape(p, s);
  strokeD(p, s, 4.5, 0, 11, 0, 0.7, 0.6);
  eyes(p, s, 8, 3.4, 0.9);
  if (b > 0) {
    for (let j = 0; j < 3; j++) {
      p.arc(13, 0, 4 + j * 2.6 + (1 - b) * 3, 1, -0.9, 1.8, LIGHT, b * (1 - j * 0.25) * s.alpha);
    }
  }
}

/** Card 04, Gullet Sack: a small head on a large sack, with a whip tail. */
function g04(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, o = clamp01(s.open), g = s.gulp, br = 0.5 + 0.5 * Math.sin(t * 1.4);
  const rx = 5.5 + o * 5 + g * 3 + br * 0.6 * (1 - sw) - sw;
  const ry = 4.5 + o * 6.5 + g * 3.5 + br * 0.6 * (1 - sw) - sw * 1.3, cx = 1 - o;
  wave(SP, cx - rx + 1.5, -18, 10, 0.8 + 2.2 * sw, 0.8, t * (2.5 + 5 * sw), 1.2);
  sTube(SP, 10, 1.7, 1, 1, 0.3); shape(p, s);
  sEll(cx, 0, rx, ry); shape(p, s, 0.92);
  p.arc(cx - rx * 0.2, 0, (rx * 0.55 + ry * 0.75) / 2, 0.6, -1.2, 2.4, DARK, 0.4 * s.alpha);
  if (g > 0) p.disc(cx - 1, 0, 2.2, DARK, 0.65 * g * s.alpha);
  if (o > 0.05) { sEll(cx + rx * 0.45, 0, rx * 0.5, ry * 0.72 * o); shapeDark(p, s, 0.95); }
  const hx = cx + rx - 1;
  put(PC, 0, hx, -2.4, hx + 4.5, -0.7);
  put(PC, 4, hx + 4.8, 0, hx + 4.5, 0.7);
  PC[8] = hx; PC[9] = 2.4;
  sPoly(PC, 5); shape(p, s);
  eyes(p, s, hx + 1.8, 1.1, 0.7);
}

// ===========================================================================
// B. THE MURKHIDE, the ambusher (cards 05 to 08)
// ===========================================================================

/** The target of a reach in local units, or the page's default point. */
function tgtX(s: MonsterState): number { return s.hasTarget ? s.tx : 22; }
function tgtY(s: MonsterState): number { return s.hasTarget ? s.ty : 0; }

/** Card 05, Trapdoor: a flat disc on the trench floor, with a lid. */
function m05(p: MonsterPen, s: MonsterState): void {
  const t = s.t, hid = s.hide, vis = 1 - hid, gx = tgtX(s), gy = tgtY(s);
  if (hid > 0.02) {
    p.dashed(0, 0, 9.5, 0.9, 15, 0.4, LIGHT, 0.32 * hid * s.alpha);
    const gl = 0.35 + 0.35 * Math.max(0, Math.sin(t * 1.1)) + 0.3 * s.tell;
    p.disc(6.5, -1.8, 0.75, LIGHT, gl * hid * s.alpha);
    p.disc(6.5, 1.8, 0.75, LIGHT, gl * hid * s.alpha);
  }
  if (vis < 0.02) return;
  const al = s.alpha;
  s.alpha = al * vis;
  for (let sd = -1; sd <= 1; sd += 2) {
    for (let j = 0; j < 3; j++) {
      const a = sd * (1.05 + j * 0.62) + Math.sin(t * 9 * s.swim + j * 2.1 + (sd > 0 ? PI : 0)) * 0.25 * s.swim, k2 = a + sd * 0.3;
      limb3(p, s, Math.cos(a) * 8.5, Math.sin(a) * 8.5, Math.cos(a) * 12.5, Math.sin(a) * 12.5,
        Math.cos(k2) * 14, Math.sin(k2) * 14, 0.9);
    }
  }
  if (s.reach > 0) {
    for (let sd = -1; sd <= 1; sd += 2) {
      const bx = 7, by = sd * 3, tx = bx + (gx - bx) * s.reach, ty = by + (gy + sd * 2.2 - by) * s.reach;
      const dx = tx - bx, dy = ty - by, l = Math.hypot(dx, dy) || 1;
      limb3(p, s, bx, by, bx + dx * 0.5 - dy / l * sd * 3.5, by + dy * 0.5 + dx / l * sd * 3.5, tx, ty, 1.3);
      const ha = Math.atan2(dy, dx);
      p.arc(tx - dx / l * 1.4, ty - dy / l * 1.4, 1.6, 0.9, sd < 0 ? ha + 1.2 : ha - 1.6, 2.8, LIGHT, s.alpha);
    }
  }
  const lid = clamp01(s.reach * 1.5 + s.grab);
  sBlob(0, 0, 9.5); shape(p, s);
  if (lid > 0.02) {
    p.disc(0, 0, 7.8, DARK, 0.95 * lid * s.alpha);
    for (let sd = -1; sd <= 1; sd += 2) p.disc(6.7, sd * 1.7, 0.9, LIGHT, s.alpha);
    const lx = -9.5 - lid * 2.5;
    sEll(lx, 0, 0.6 + 2.6 * lid, 9.5); shape(p, s);
    for (let j = -2; j <= 2; j++) strokeD(p, s, lx, j * 3.4, lx + (0.4 + 2 * lid) * 0.8, j * 3.4, 0.5, 0.6);
  } else {
    for (let j = 0; j < 6; j++) {
      const a = j * PI / 3 + 0.5;
      strokeD(p, s, Math.cos(a) * 2.5, Math.sin(a) * 2.5, Math.cos(a) * 8, Math.sin(a) * 8, 0.6, 0.55);
    }
    p.disc(6.3, -1.1, 0.55, DARK, 0.9 * s.alpha); p.disc(6.3, 1.1, 0.55, DARK, 0.9 * s.alpha);
    p.disc(5.4, -2.7, 0.55, DARK, 0.9 * s.alpha); p.disc(5.4, 2.7, 0.55, DARK, 0.9 * s.alpha);
  }
  s.alpha = al;
}

/** Card 06, Mantis Fold: a long body, two folded striking arms, eyes on stalks. */
function m06(p: MonsterPen, s: MonsterState): void {
  const t = s.t, hid = s.hide, vis = 1 - 0.85 * hid, gx = tgtX(s), gy = tgtY(s), r = s.reach;
  const al = s.alpha;
  s.alpha = al * vis;
  wave(SP, 9, -12, 8, 0.4 + 1.3 * s.swim, 0.8, t * (2 + 4 * s.swim), 1.2);
  sTube(SP, 8, 4.2, 0.55, 1, 0.35);
  const ex = SP[14], ey = SP[15];
  for (let sd = -1; sd <= 1; sd++) sEll(ex - 2, ey + sd * 1.8, 2.4, 0.9, sd * 0.5);
  shape(p, s);
  for (let i = 2; i < 7; i++) {
    const x = SP[i * 2], y = SP[i * 2 + 1], qx = SP[i * 2 - 2], qy = SP[i * 2 - 1];
    const a = Math.atan2(y - qy, x - qx) + PI / 2, w = 3.4 * (1 - 0.55 * i / 7);
    strokeD(p, s, x + Math.cos(a) * w, y + Math.sin(a) * w, x - Math.cos(a) * w, y - Math.sin(a) * w, 0.55, 0.7);
  }
  for (let sd = -1; sd <= 1; sd += 2) {
    const shx = 7, shy = sd * 2.6;
    const dx = gx - shx, dy = gy + sd * 1.5 - shy, L = Math.hypot(dx, dy) || 1, nx = -dy / L * sd, ny = dx / L * sd;
    const elx = lerp(1.2, shx + dx * 0.45 + nx * 3, r), ely = lerp(sd * 6, shy + dy * 0.45 + ny * 3, r);
    const clx = lerp(6.8, shx + dx, r), cly = lerp(sd * 6.8, shy + dy, r);
    sc(shx, shy, elx, ely, 2.4); sc(elx, ely, clx, cly, 2.4);
    shape(p, s);
    sBlob(clx, cly, 1.5); shape(p, s);
  }
  s.alpha = al;
  for (let sd = -1; sd <= 1; sd += 2) {
    const ex2 = 12.6 + 0.5 * Math.sin(t * 2.3 + sd), ey2 = sd * (3.2 + 0.7 * Math.sin(t * 3.1 + sd * 2));
    strokeL(p, s, 9.5, sd * 1.2, ex2, ey2, 0.7, Math.max(vis, 0.6));
    p.disc(ex2, ey2, 1.25, LIGHT, Math.max(vis, 0.75) * s.alpha);
    p.disc(ex2 + 0.3, ey2, 0.55, DARK, 0.9 * s.alpha);
  }
}

/** The scatter of faint dots of a hidden Ink Veil: the page's seed 7. */
const VEIL_DOTS: Float32Array = (() => {
  const r = mulberry32(7);
  const out = new Float32Array(15 * 3);
  for (let j = 0; j < 15; j++) {
    const ang = r() * TAU, rr = Math.sqrt(r());
    out[j * 3] = Math.cos(ang) * rr * 10; out[j * 3 + 1] = Math.sin(ang) * rr * 6; out[j * 3 + 2] = 0.7 + r() * 0.5;
  }
  return out;
})();

/** Card 07, Ink Veil: a mantle with a fin fringe and an ink cloud. */
function m07(p: MonsterPen, s: MonsterState): void {
  const t = s.t, hid = s.hide, vis = 1 - hid, gx = tgtX(s), gy = tgtY(s), r = s.reach;
  if (s.ink > 0 && s.ink < 1) {
    const e = s.ink;
    for (let j = 0; j < 5; j++) {
      const x = -4 - e * 16 - j * 3.2, y = (j % 2 ? 1 : -1) * (2 + j * 1.6) * (0.4 + e), rr = 2.5 + e * 7 + j * 0.6;
      p.cap(x, y, x, y, 2 * rr, DARK, 0.82 * (1 - e) * s.alpha);
    }
  }
  if (hid > 0.02) {
    for (let j = 0; j < 15; j++) {
      p.disc(VEIL_DOTS[j * 3], VEIL_DOTS[j * 3 + 1], VEIL_DOTS[j * 3 + 2], LIGHT,
        (0.14 + 0.12 * Math.sin(t * 1.5 + j)) * hid * s.alpha);
    }
  }
  if (vis < 0.02) return;
  const al = s.alpha;
  s.alpha = al * vis;
  if (r > 0) {
    for (let sd = -1; sd <= 1; sd += 2) {
      const tx = 10 + (gx - 10) * r, ty = sd + (gy + sd * 1.5 - sd) * r, dx = tx - 10, dy = ty - sd, l = Math.hypot(dx, dy) || 1;
      limb3(p, s, 10, sd, 10 + dx * 0.5 - dy / l * sd * 2, sd + dy * 0.5 + dx / l * sd * 2, tx, ty, 0.9);
      sBlob(tx, ty, 1.5); shape(p, s);
    }
  }
  for (let j = -2; j <= 2; j++) {
    const w = Math.sin(t * 3 + j);
    limb3(p, s, 9.5, j * 0.9, 12, j * 1.2 + w * 0.5, 14.2, j * 1.6 + w * 0.8, 0.7);
  }
  // The fringe: a ripple runs round the mantle.
  const rate = 4 + 7 * s.swim;
  for (let i = 0; i < 20; i++) {
    const th = i / 20 * TAU, rr = 1 + 0.09 * Math.sin(12 * th - t * rate);
    sBlob(Math.cos(th) * 10.6 * rr - 1, Math.sin(th) * 6.4 * rr, 1.5);
  }
  shape(p, s, 0.45);
  sEll(-1, 0, 10.3, 6.1); shape(p, s);
  for (let x = -5; x <= 2; x += 3.5) {
    quadD(p, s, x, -4.4, x + 1.4, 0, x, 4.4, 0.8, 0.35 + 0.25 * Math.sin(t * 2 + x));
  }
  eyes(p, s, 6.5, 4.2, 0.9);
  s.alpha = al;
}

/** Card 08, Lure Lamp: only a light on a stalk shows while it hides. */
function m08(p: MonsterPen, s: MonsterState): void {
  const t = s.t, hid = s.hide, vis = 1 - hid, gx = tgtX(s), gy = tgtY(s);
  const d = Math.hypot(gx, gy), ta = Math.atan2(gy, gx), r = s.reach;
  const off = r * Math.max(0, d - 15);
  p.push(Math.cos(ta) * off, Math.sin(ta) * off, 0, 1);
  const rate = 1.2 + 9 * s.tell, bl = 0.55 + 0.45 * Math.sin(t * rate * 2.2);
  const lx = 15 + Math.sin(t * 1.2) * 1.2, ly = -6 + Math.cos(t * 1.5);
  quadL(p, s, 8, -3.5, 12, -11, lx, ly, 0.7, 0.35 + 0.5 * vis);
  p.glow(lx, ly, 6, 0.45 * bl * s.alpha);
  p.disc(lx, ly, 1.3, LIGHT, (0.6 + 0.4 * bl) * s.alpha);
  if (vis > 0.02) {
    const al = s.alpha;
    s.alpha = al * vis;
    wave(SP, -2, -15, 7, 0.5 + 1.5 * s.swim, 0.9, t * (2 + 4 * s.swim), 1.3);
    sTube(SP, 7, 4.5, 1, 1, 0.3);
    sFin(SP[12], SP[13], PI, 3, 2.6);
    shape(p, s);
    sEll(3, 0, 8.4, 7.2); shape(p, s);
    const o = clamp01(s.open);
    if (o > 0.03) {
      put(PC, 0, 3.5, 0, 12.5, -4.5 * o - 1);
      put(PC, 4, 13.4, 0, 12.5, 4.5 * o + 1);
      sPoly(PC, 4); shapeDark(p, s, 0.95);
      for (let j = 0; j < 4; j++) {
        for (let sd = -1; sd <= 1; sd += 2) {
          strokeL(p, s, 8.5 + j * 1.1, sd * (3.4 * o + 0.6), 8.5 + j * 1.1, sd * (3.4 * o + 0.6 - 2), 0.6);
        }
      }
    } else {
      quadD(p, s, 5, 1.5, 9, 4, 11.4, 1, 0.8);
      for (let j = 0; j < 3; j++) strokeL(p, s, 7 + j * 1.4, 2.8 - j * 0.5, 7.3 + j * 1.4, 1.6 - j * 0.5, 0.5);
    }
    p.disc(6, -3.6, 0.9, DARK, 0.9 * s.alpha);
    s.alpha = al;
  }
  p.pop();
}

// ===========================================================================
// C. THE QUILLBACK, the bio torpedo launcher (cards 09 to 12)
// ===========================================================================

/** How much of a front quill or sac stands: 0 once spent, then the regrowth. */
function spentScale(s: MonsterState, j: number): number {
  return s.spent > j ? s.regrow : 1;
}

/** Card 09, Quill Spine: an oval body with a crest of quills. */
function q09(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, sv = s.swell, k = 1 + 0.08 * sv, raise = clamp01(0.3 + 0.7 * sv - 0.25 * sw);
  p.push(0, 0, 0, k);
  for (let sd = -1; sd <= 1; sd += 2) {
    for (let j = 0; j < 6; j++) {
      const ph = PI * (0.36 + j * 0.12) * sd, rx = -1 + Math.cos(ph) * 9.5, ry = Math.sin(ph) * 5.8;
      const a = lerpAng(PI - sd * 0.25, ph, raise) + 0.08 * Math.sin(t * 1.6 + j + sd);
      let len = 6.5 + 1.5 * sv;
      if (j === 0) len *= spentScale(s, sd < 0 ? 0 : 1);
      if (len > 0.3) limb2(p, s, rx, ry, rx + Math.cos(a) * len, ry + Math.sin(a) * len, 0.8);
    }
  }
  sEll(-1, 0, 9.5, 5.8);
  sPoly(Q09_HEAD, 3);
  shape(p, s);
  strokeD(p, s, -8, 0, 6, 0, 0.6, 0.4);
  eyes(p, s, 8.4, 1.7, 0.7);
  p.pop();
}
const Q09_HEAD = [7.5, -2.6, 12.8, 0, 7.5, 2.6];

/** Card 10, Brood Sac: a soft body with three pairs of glowing sacs. */
function q10(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, sv = s.swell;
  wave(SP, 10, -12, 8, 0.4 + sw, 0.8, t * (2 + 3 * sw), 1.3);
  sTube(SP, 8, 4.8, 0.55, 1, 0.4); shape(p, s);
  for (let i = 0; i < 3; i++) {
    const x = i === 0 ? 5 : i === 1 ? 0.5 : -4;
    for (let sd = -1; sd <= 1; sd += 2) {
      let r = 2.3 * (1 + 0.1 * Math.sin(t * 2.4 + i * 1.3 + sd)) * (1 + (i === 0 ? 0.25 * sv : 0));
      if (i === 0) r *= spentScale(s, sd < 0 ? 0 : 1);
      if (r < 0.2) continue;
      const y = sd * (3.9 - i * 0.4);
      p.glow(x, y, r * 2.4, (0.12 + 0.35 * sv) * s.alpha);
      sBlob(x, y, r); shape(p, s, 0.9);
      p.disc(x, y, 0.7, DARK, 0.8 * s.alpha);
    }
  }
  eyes(p, s, 9, 1.9, 0.65);
}

/** Card 11, Siphon Tube: a ringed tube with a siphon at the bow. */
function q11(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, sv = s.swell, bp = sv > 0 ? 1 - sv : (t * 0.25) % 1, bh = sv > 0 ? 1.7 : 0.6;
  wave(SP, 9, -15, 10, 0.3 + 0.9 * sw, 0.7, t * (1.6 + 3 * sw), 1.2);
  // The half width is 3 - 0.8 u plus the bulge: the kit's form with b = 0.8 / 3.
  sTube(SP, 10, 3, 0.8 / 3, 1, 0, bh, bp); shape(p, s);
  for (let i = 1; i < 10; i++) {
    const x = SP[i * 2], y = SP[i * 2 + 1], a = Math.atan2(y - SP[i * 2 - 1], x - SP[i * 2 - 2]) + PI / 2, w = 2.6 - 0.7 * i / 9;
    strokeD(p, s, x + Math.cos(a) * w, y + Math.sin(a) * w, x - Math.cos(a) * w, y - Math.sin(a) * w, 0.5, 0.6);
  }
  const ex = SP[18], ey = SP[19];
  for (let sd = -1; sd <= 1; sd++) strokeL(p, s, ex, ey, ex - 3.5, ey + sd * 2 + Math.sin(t * 3 + sd), 0.6);
  sEll(10.5, 0, 2.3, 3.9 + 1.2 * sv); shape(p, s);
  sEll(11.4, 0, 1.0, 2.4 + 0.8 * sv); shapeDark(p, s, 0.95);
  eyes(p, s, 7.6, 2.3, 0.6);
}

/** The six buds of card 12: the angle and the bud a launch spends (-1 none). */
const BUDS = [0.38, 0, -0.38, 1, 0.95, -1, -0.95, -1, 1.5, -1, -1.5, -1];

/** Card 12, Bud Gourd: a wide gourd with a ring of buds round its mouth. */
function q12(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, sv = s.swell, pp = Math.max(0, Math.sin(t * (2 + 3.5 * sw))), k = 1 - 0.07 * pp * (0.4 + sw);
  wave(SP, -6, -16, 6, 0.6 + 1.6 * sw, 0.9, t * (2.4 + 4 * sw), 1.2);
  sTube(SP, 6, 2.2, 1, 1, 0.3);
  sFin(SP[10], SP[11], PI, 3, 2.8);
  shape(p, s);
  sEll(1, 0, 8.5, 7.4 * k); shape(p, s);
  for (let sd = -1; sd <= 1; sd += 2) {
    quadL(p, s, 9, sd * 2, 15, sd * (3 + Math.sin(t * 2 + sd)), 18 + Math.sin(t * 1.4 + sd),
      sd * (7 + 1.5 * Math.sin(t * 1.7 + sd)), 0.6, 0.85);
  }
  for (let i = 0; i < 6; i++) {
    const a = BUDS[i * 2], j = BUDS[i * 2 + 1];
    const x = Math.cos(a) * 8.4 + 1.4, y = Math.sin(a) * 8.4 * k;
    const r = 1.9 * (j >= 0 ? (1 + 0.3 * sv) * spentScale(s, j) : 1);
    if (r < 0.2) continue;
    if (j >= 0) p.glow(x, y, r * 2.4, 0.35 * sv * s.alpha);
    p.disc(x, y, r + p.ew, DARK, 0.9 * s.alpha);
    p.disc(x, y, r, LIGHT, s.alpha);
    p.disc(x, y, 0.55, DARK, 0.7 * s.alpha);
  }
  sEll(8.6, 0, 1.4, 2.6); shapeDark(p, s, 0.95);
  eyes(p, s, 4.5, 3.4, 0.7);
}

// ===========================================================================
// D. THE THRUMMER, the area discharge (cards 13 to 16)
// ===========================================================================

/** Card 13, Discharge Ray: a ray with beating wings and a long tail. */
function d13(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, ch = s.charge, pu = s.pulse;
  p.glow(0, 0, 18, 0.3 * ch * s.alpha);
  const fl = Math.sin(t * (1.6 + 3.4 * sw));
  const span = 12.5 * (1 - 0.17 * (0.5 + 0.5 * fl)) * (pu >= 0 ? 1 - 0.1 * (1 - pu) : 1), tip = -2.5 - 1.2 * fl;
  wave(SP, -7, -21, 8, 0.6 + 1.2 * sw, 0.7, t * (2 + 3 * sw), 1.3);
  for (let i = 0; i < 7; i++) sc(SP[i * 2], SP[i * 2 + 1], SP[i * 2 + 2], SP[i * 2 + 3], 1.2);
  sFin(SP[14], SP[15], PI, 2.5, 1.2, 1);
  shape(p, s, 1, 1);
  // The wings: a fan of capsules from the body to the wing points.
  for (let sd = -1; sd <= 1; sd += 2) {
    sc(2, sd * 1.5, 1.5, sd * span * 0.55, 4.2);
    sc(0, sd * 2, tip, sd * (span - 1.4), 2.8);
    sc(-3, sd * 1.5, -4.5, sd * span * 0.5, 3.2);
  }
  sEll(1, 0, 8.6, 3.2);
  shape(p, s);
  for (let sd = -1; sd <= 1; sd += 2) limb2(p, s, 9, sd * 2.2, 12.2, sd * 2.8, 1.1);
  eyes(p, s, 5.5, 3.2, 0.7);
  if (ch > 0) {
    const fr = Math.floor(t * 12), n = 1 + Math.floor(ch * 6);
    for (let j = 0; j < n; j++) {
      const h = (fr * 7 + 1) * 31 + j * 5;
      const sd = hash(h, 1) < 0.5 ? -1 : 1, x0 = -4 + hash(h, 2) * 10, y0 = sd * (2 + hash(h, 3) * span * 0.5);
      const x1 = x0 + (hash(h, 4) - 0.5) * 8, y1 = sd * (2 + hash(h, 5) * span * 0.7);
      zig(p, s, x0, y0, x1, y1, (fr + j) * 13, 1.2, 4, 0.6, 0.5 + 0.5 * ch);
    }
  }
  if (pu >= 0) { ringPulse(p, s, pu, 2.2); zap(p, s, pu); }
}

/** Card 14, Twin Drum: two round bladders side by side, with membranes. */
function d14(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, ch = s.charge, pu = s.pulse;
  const r = 6 + 2.4 * ch - (pu >= 0 ? 1.4 * (1 - pu) : 0) + 0.3 * Math.sin(t * 1.5), sp = 4.6 + 0.6 * Math.sin(t * 4 * sw);
  p.glow(0, 0, r * 3, 0.3 * ch * s.alpha);
  for (let sd = -1; sd <= 1; sd += 2) {
    sEll(-r - 1, sd * sp * 0.7, 2.6, 1.2, sd * (0.5 + 0.4 * Math.sin(t * 8 * sw + sd)));
    shape(p, s);
  }
  sBlob(0, -sp, r); sBlob(0, sp, r); shape(p, s);
  for (let sd = -1; sd <= 1; sd += 2) {
    p.ring(0, sd * sp, r * 0.6 + ch * 0.6 * Math.sin(t * 40 + sd), 0.9, DARK, 0.8 * s.alpha);
    p.disc(0, sd * sp, 1.1, DARK, 0.85 * s.alpha);
  }
  eyes(p, s, r * 0.8, 1.4, 0.6);
  if (pu >= 0) {
    for (let j = 0; j < 3; j++) {
      const q = pu - j * 0.12;
      if (q <= 0) continue;
      p.ring(0, 0, 70 * eout(q), (3 - j) * (1 - pu) + 0.4, LIGHT, 0.8 * (1 - pu) * (1 - j * 0.3) * s.alpha);
    }
    zap(p, s, pu);
  }
}

/** Card 15, Coil Eel: an eel in a coil that opens to an S when it swims. */
function d15(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, ch = s.charge, pu = s.pulse, N = 22, tight = 1 - 0.15 * ch, spin = t * 0.25;
  for (let i = 0; i <= N; i++) {
    const u = i / N, th = spin + u * 3.3 * PI, rr = (10.5 - u * 8) * tight;
    SP[i * 2] = lerp(Math.cos(th) * rr, 13 - u * 30, sw);
    SP[i * 2 + 1] = lerp(Math.sin(th) * rr, 2.6 * Math.sin(u * 7.5 - t * 6) * (0.3 + u), sw);
  }
  p.glow(0, 0, 16, 0.25 * ch * s.alpha);
  sTube(SP, N + 1, 2.1, 0.55, 1, 0.35); shape(p, s);
  for (let i = 3; i < N; i += 2) p.disc(SP[i * 2], SP[i * 2 + 1], 0.45, DARK, 0.8 * s.alpha);
  const ha = Math.atan2(SP[1] - SP[3], SP[0] - SP[2]);
  p.disc(SP[0] + Math.cos(ha + 1.2) * 1.2, SP[1] + Math.sin(ha + 1.2) * 1.2, 0.6, DARK, 0.9 * s.alpha);
  if (ch > 0) {
    const sw2 = (t * 1.6) % 1;
    for (let i = 0; i <= N; i++) {
      const b = Math.exp(-((i / N - sw2) ** 2) / 0.006);
      if (b > 0.05) {
        p.glow(SP[i * 2], SP[i * 2 + 1], 3.5, 0.6 * ch * b * s.alpha);
        p.disc(SP[i * 2], SP[i * 2 + 1], 0.8, LIGHT, ch * b * s.alpha);
      }
    }
  }
  if (pu >= 0) {
    for (let j = 0; j < 12; j++) {
      const a = j * TAU / 12 + 0.2, r1 = 11 + 26 * eout(pu);
      strokeL(p, s, Math.cos(a) * 11, Math.sin(a) * 11, Math.cos(a) * r1, Math.sin(a) * r1, 0.9, 1 - pu);
    }
    ringPulse(p, s, pu, 1.6); zap(p, s, pu);
  }
}

/** Card 16, Lamp Crown: a crown of eight points with a lamp at each. */
function d16(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, ch = s.charge, pu = s.pulse, pp = Math.max(0, Math.sin(t * (1.5 + 3 * sw))), k = 1 - 0.08 * pp;
  sBlob(0, 0, 7.4 * k);
  for (let i = 0; i < 8; i++) {
    const a = i * PI / 4;
    spoke(0, 0, Math.cos(a) * 10 * k, Math.sin(a) * 10 * k, 3.4 * k);
  }
  shape(p, s);
  p.disc(0, 0, 3.2, DARK, 0.85 * s.alpha);
  const lit = ch > 0 ? Math.floor(ch * 8.999) + 1 : pu >= 0 ? 8 : 0;
  for (let j = 0; j < 8; j++) {
    const a = j * PI / 4, x = Math.cos(a) * 7.6 * k, y = Math.sin(a) * 7.6 * k;
    const on = j < lit || (lit === 0 && Math.floor(t * 2) % 8 === j);
    if (on) {
      p.glow(x, y, 4, (lit ? 0.55 : 0.3) * s.alpha);
      p.disc(x, y, 1.25, LIGHT, s.alpha);
      p.ring(x, y, 1.25, 0.5, DARK, 0.8 * s.alpha);
    } else {
      p.disc(x, y, 1.2, DARK, 0.85 * s.alpha);
    }
  }
  if (pu >= 0) {
    for (let j = 0; j < 8; j++) {
      const a = j * PI / 4, r1 = 10 + 55 * eout(pu);
      strokeL(p, s, Math.cos(a) * 10, Math.sin(a) * 10, Math.cos(a) * r1, Math.sin(a) * r1, 1.2, 1 - pu);
    }
    ringPulse(p, s, pu, 1.4); zap(p, s, pu);
  }
}

// ===========================================================================
// E. THE MYRIAD, the swarm (cards 17 to 20)
// ===========================================================================

/** The swarm buffer: x, y, heading of up to 32 bodies. */
const SW = new Float32Array(32 * 3);

/** How many of `n` bodies a Myriad keeps (ruling R-e): one at least. */
function kept(s: MonsterState, n: number): number {
  return Math.max(1, Math.min(n, Math.ceil(n * clamp01(s.bodies))));
}

/** The bodies of a swarm (`swarm()`), into `SW`. Answers the alpha. */
function swarm(s: MonsterState, N: number, R0: number): number {
  const t = s.t, sw = s.swim, g = s.gather, gx = s.hasTarget ? s.tx : 0, gy = s.hasTarget ? s.ty : 0;
  let al = 1;
  for (let i = 0; i < N; i++) {
    const rr = R0 * Math.sqrt((i + 0.5) / N), th = i * 2.39996 + t * 0.35 * (1 - sw);
    let x = Math.cos(th) * rr + 1.5 * Math.sin(t * 1.3 + i * 1.7), y = Math.sin(th) * rr + 1.5 * Math.cos(t * 1.1 + i * 2.3), a = th + PI / 2;
    if (sw > 0) { x = lerp(x, x * 1.3 - Math.abs(y) * 0.9 - 2, sw); y = lerp(y, y * 0.65, sw); a = lerpAng(a, 0.3 * Math.sin(t * 5 + i), sw); }
    if (g > 0) {
      const ra = i / N * TAU + t * 1.6, rd = 8.5 + 1.6 * Math.sin(t * 6 + i * 1.9) - 2.2 * s.bite, e = ease(g);
      x = lerp(x, gx + Math.cos(ra) * rd, e); y = lerp(y, gy + Math.sin(ra) * rd, e); a = lerpAng(a, ra + PI, e);
    }
    if (g < 0) { x *= 1 - 2.4 * g; y *= 1 - 2.4 * g; al = 1 + 0.7 * g; }
    SW[i * 3] = x; SW[i * 3 + 1] = y; SW[i * 3 + 2] = a;
  }
  return al;
}

/** Card 17, Dot Cloud: 24 dots that mill round one centre under a glow. */
function e17(p: MonsterPen, s: MonsterState): void {
  const n = kept(s, 24), al = swarm(s, 24, 11);
  let cx = 0, cy = 0;
  for (let i = 0; i < 24; i++) { cx += SW[i * 3] / 24; cy += SW[i * 3 + 1] / 24; }
  p.glow(cx, cy, 16, 0.12 * s.alpha);
  for (let i = 0; i < n; i++) p.disc(SW[i * 3], SW[i * 3 + 1], 1.15, LIGHT, 0.95 * s.alpha * al);
}

/** Card 18, Ribbon School: 26 small bodies on one ribbon. */
function e18(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, g = s.gather, gx = s.hasTarget ? s.tx : 0, gy = s.hasTarget ? s.ty : 0, N = 26;
  const n = kept(s, N), al = s.alpha;
  if (g < 0) s.alpha = al * (1 + 0.7 * g);
  for (let i = 0; i < n; i++) {
    const u = (i / N + t * 0.08) % 1;
    ribbonAt(u, t, sw, g, gx, gy, s.bite, 0);
    ribbonAt(u + 0.01, t, sw, g, gx, gy, s.bite, 2);
    sEll(RB[0], RB[1], 1.7, 0.75, Math.atan2(RB[3] - RB[1], RB[2] - RB[0]));
    shape(p, s, 1, 0.6);
  }
  s.alpha = al;
}
const RB = new Float32Array(4);
function ribbonAt(u: number, t: number, sw: number, g: number, gx: number, gy: number, bite: number, o: number): void {
  let x = Math.cos(u * TAU) * 11, y = Math.sin(u * TAU * 2) * 5;
  x = lerp(x, 9 - u * 30, sw); y = lerp(y, 3 * Math.sin(u * 9 - t * 7), sw);
  if (g > 0) {
    const e = ease(g), an = u * 4 * PI + t * 2.2, rd = 5 + u * 7 - bite * 1.5;
    x = lerp(x, gx + Math.cos(an) * rd, e); y = lerp(y, gy + Math.sin(an) * rd, e);
  }
  if (g < 0) { x *= 1 - 2.4 * g; y *= 1 - 2.4 * g; }
  RB[o] = x; RB[o + 1] = y;
}

/** Card 19, Ring Pack: two rings of teeth round a dark centre. */
function e19(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, g = s.gather, e = ease(Math.max(0, g)), k = g < 0 ? 1 - 2.4 * g : 1;
  const gx = s.hasTarget ? s.tx : 0, gy = s.hasTarget ? s.ty : 0, al = s.alpha;
  if (g < 0) s.alpha = al * (1 + 0.7 * g);
  const cx = lerp(-2 * sw, gx, e), cy = lerp(0, gy, e);
  p.cap(cx, cy, cx, cy, 6.8, DARK, 0.45 * s.alpha);
  const n = kept(s, 21);
  for (let i = 0; i < Math.min(n, 14); i++) {
    const an = i * TAU / 14 + t * 0.8;
    const rx = lerp(9.5 * (1 + 0.25 * sw), 7.5 - s.bite * 1.5, e) * k, ry = lerp(9.5 * (1 - 0.25 * sw), 7.5 - s.bite * 1.5, e) * k;
    wedge(cx + Math.cos(an) * rx, cy + Math.sin(an) * ry, lerpAng(an + PI / 2, an + PI, e));
    shape(p, s, 1, 0.45);
  }
  for (let i = 0; i < n - 14; i++) {
    const an = i * TAU / 7 - t * 1.2, r = lerp(5, 4.5, e) * k;
    wedge(cx + Math.cos(an) * r, cy + Math.sin(an) * r, lerpAng(an - PI / 2, an + PI, e));
    shape(p, s, 1, 0.45);
  }
  s.alpha = al;
}
/** One tooth of the Ring Pack: a wedge, bow at `a`. */
function wedge(x: number, y: number, a: number): void {
  const c = Math.cos(a), sn = Math.sin(a);
  sc(x - c * 1.1, y - sn * 1.1, x + c * 1.2, y + sn * 1.2, 1.2);
  sc(x - c * 1.4, y - sn * 1.4, x - c * 0.6, y - sn * 0.6, 1.8);
}

/** Card 20, Comma Flock: 14 small bodies, each a head and a curved tail. */
function e20(p: MonsterPen, s: MonsterState): void {
  const t = s.t, sw = s.swim, g = s.gather, gx = s.hasTarget ? s.tx : 0, gy = s.hasTarget ? s.ty : 0, al = s.alpha;
  if (g < 0) s.alpha = al * (1 + 0.7 * g);
  const n = kept(s, 14);
  for (let i = 0; i < n; i++) {
    const row = Math.floor((i + 1) / 2), sd = i === 0 ? 0 : (i % 2 ? 1 : -1), ma = i / 14 * TAU + t * 0.6;
    let x = lerp(Math.cos(ma) * 9, 7 - row * 2.9 + Math.sin(t * 2 + i), sw);
    let y = lerp(Math.sin(ma) * 7.5, sd * row * 2.4 + Math.cos(t * 2.3 + i) * 0.6, sw);
    let a = lerpAng(ma + PI / 2, 0, sw);
    if (g > 0) {
      const ph = (t * 1.5 + i / 14) % 1, dv = ph < 0.3 ? ph / 0.3 : 1 - (ph - 0.3) / 0.7, ra = i / 14 * TAU + t * 0.5;
      const rx = gx + Math.cos(ra) * 14, ry = gy + Math.sin(ra) * 14, e = ease(g);
      const dvx = lerp(rx, gx, ease(dv) * 0.85), dvy = lerp(ry, gy, ease(dv) * 0.85);
      x = lerp(x, dvx, e); y = lerp(y, dvy, e); a = lerpAng(a, ph < 0.3 ? ra + PI : ra, e);
    }
    if (g < 0) { x *= 1 - 2.4 * g; y *= 1 - 2.4 * g; }
    const tw = Math.sin(t * 10 + i) * 1.2, ca = Math.cos(a), sa = Math.sin(a);
    quadL(p, s, x, y, x - ca * 2 - sa * tw, y - sa * 2 + ca * tw, x - ca * 3.8, y - sa * 3.8, 0.8, 1, 3);
    sBlob(x, y, 1.05); shape(p, s, 1, 0.5);
  }
  s.alpha = al;
}

// ===========================================================================
// The tables
// ===========================================================================

/** One body draw. */
export type MonsterBody = (p: MonsterPen, s: MonsterState) => void;

/** The bodies by kind (Gulper, Murkhide, Quillback, Thrummer, Myriad) and
 *  design, in the card order of round 109. */
export const MONSTER_BODIES: readonly (readonly MonsterBody[])[] = [
  [g01, g02, g03, g04],
  [m05, m06, m07, m08],
  [q09, q10, q11, q12],
  [d13, d14, d15, d16],
  [e17, e18, e19, e20],
];

/** How far a body reaches past its origin, in local units, by kind: the cull
 *  pad. The Thrummer's discharge ring opens to 70; a Murkhide's reach and a
 *  Myriad's wrap go to the target. */
export const MONSTER_EXTENT = [28, 60, 30, 78, 40] as const;

/** The swim of a MOVE by kind (`moveSwim` of the page). */
const MOVE_SWIM = [1, 0.45, 0.7, 0.8, 1] as const;

/** The acts of the wire (`NpcAct`), as numbers, so this leaf names no wire type. */
export const ACT = {
  none: 0, idle: 1, move: 2, tell: 3, strike: 4, hold: 5, launch: 6, pulse: 7, gather: 8, scatter: 9,
} as const;

/** How long the page plays a discharge, in seconds (`pulse` of card 13). */
const PULSE_SEC = 1.25;
/** One bite of the page's attack, in seconds: the cycle a STRIKE repeats. */
const BITE_SEC = 0.9;

/**
 * Writes the state of a body from its act (R2719, v2-the-deep.md §8.4).
 *
 * `phase` is the seconds since the act began on this client. `share` is the
 * share of a timed act that has run, 0 to 1, from `npc_act_left_ms`, or -1
 * for an act with no clock. The tell, the strike and the pulse follow the
 * attack sequence of each card of the page.
 */
export function actPose(kind: number, act: number, phase: number, share: number, s: MonsterState): void {
  restState(s);
  const run = share >= 0 ? share : -1;
  switch (kind) {
    case 0: // The Gulper: it opens and bites.
      if (act === ACT.move) s.swim = MOVE_SWIM[0];
      else if (act === ACT.tell) s.open = 0.6 * ease(phase / 0.5);
      else if (act === ACT.strike || act === ACT.hold) {
        const k = (phase % BITE_SEC) / BITE_SEC;
        s.open = k < 0.5 ? ease(k / 0.5) : 1 - ease((k - 0.5) / 0.12);
        s.bite = pulseAt(k, 0.55, 0.2);
        s.swim = 0.25;
        s.gulp = phase > BITE_SEC * 2 ? 0.5 : 0;
      }
      break;
    case 1: // The Murkhide: it hides, warns, strikes and grips.
      s.hide = 1;
      if (act === ACT.move) { s.hide = 0.6; s.swim = MOVE_SWIM[1]; }
      else if (act === ACT.tell) { s.tell = run >= 0 ? run : clamp01(phase / 3); s.open = 0.3 * s.tell; }
      else if (act === ACT.strike) {
        s.hide = 1 - win(phase, 0, 0.15);
        s.reach = ease(phase / 0.3);
        s.open = ease(phase / 0.4);
        s.bite = pulseAt(phase, 0.3, 0.3);
        s.ink = clamp01(phase / 2.5);
      } else if (act === ACT.hold) {
        s.hide = 0; s.reach = 1; s.grab = 1; s.open = 0.25;
        s.ink = clamp01((phase + 1) / 2.5);
      }
      break;
    case 2: // The Quillback: it swells, launches and grows its pods back.
      if (act === ACT.move) s.swim = MOVE_SWIM[2];
      else if (act === ACT.tell) s.swell = ease(phase / 0.8);
      else if (act === ACT.launch) {
        s.swim = 0.2;
        s.swell = phase < 0.4 ? 0.6 * (1 - phase / 0.4) : 0;
        s.spent = 2;
        s.regrow = run >= 0 ? ease(run) : ease((phase - 1) / 13);
      }
      break;
    case 3: // The Thrummer: it hums, then discharges.
      if (act === ACT.move) s.swim = MOVE_SWIM[3];
      else if (act === ACT.tell) { s.swim = 0.15; s.charge = run >= 0 ? run : clamp01(phase / 2); }
      else if (act === ACT.pulse || act === ACT.strike) {
        s.swim = 0.15;
        s.pulse = phase < PULSE_SEC ? phase / PULSE_SEC : -1;
      }
      break;
    case 4: // The Myriad: it streams, wraps a hull and scatters.
      if (act === ACT.move) s.swim = MOVE_SWIM[4];
      else if (act === ACT.gather || act === ACT.strike || act === ACT.hold) {
        s.swim = 0.15;
        s.gather = ease(phase / 0.8);
        if (phase > 0.8) s.bite = Math.max(0, 1 - ((phase - 0.8) % 0.275) / 0.2);
      } else if (act === ACT.scatter) s.gather = -ease(phase / 1);
      break;
    default:
      break;
  }
}

// ===========================================================================
// The quill pod (cards 09 to 12): the pod, its trail and its burst
// ===========================================================================

/** The wave path of each pod design: the lateral amplitude and the half
 *  waves of one run (`wave` of the page's designs). */
const POD_WAVE: readonly { amp: number; n: number }[] = [
  { amp: 4, n: 2 }, { amp: 9, n: 3 }, { amp: 6, n: 5 }, { amp: 7, n: 2 },
];

/** The lateral offset of a pod at run share `u` (`posAt` of the page). */
export function podLateral(design: number, u: number, ph: number): number {
  const w = POD_WAVE[design] ?? POD_WAVE[0];
  return w.amp * Math.sin(u * PI * w.n + ph) * Math.sin(u * PI);
}

/** The pod itself, at the origin, bow east. */
export function drawPod(design: number, p: MonsterPen, s: MonsterState): void {
  const t = s.t;
  switch (design) {
    case 1: { // An egg with a beating tail.
      let ax = -2.2, ay = 0;
      for (let j = 1; j < 8; j++) {
        const bx = -2.2 - j * 0.8, by = 1.2 * Math.sin(j * 0.95 - t * 20) * j / 7;
        strokeL(p, s, ax, ay, bx, by, 0.7);
        ax = bx; ay = by;
      }
      sEll(0, 0, 2.4, 2.0); shape(p, s);
      p.disc(0.3, 0, 0.8, DARK, 0.8 * s.alpha);
      return;
    }
    case 2: { // A larva of four segments that wriggle.
      for (let j = 3; j >= 0; j--) {
        const x = j === 0 ? 2.2 : j === 1 ? -0.4 : j === 2 ? -2.8 : -4.9;
        const r = j === 0 ? 1.7 : j === 1 ? 1.45 : j === 2 ? 1.2 : 0.9;
        sEll(x, Math.sin(t * 16 - j * 1.2) * 0.9 * j / 3, r, r * 0.85); shape(p, s);
      }
      p.disc(2.9, 0, 0.45, DARK, 0.9 * s.alpha);
      return;
    }
    case 3: { // A bell that pulses.
      const c2 = 0.5 + 0.5 * Math.sin(t * 15);
      for (let y = -1; y <= 1; y++) quadL(p, s, -1, y, -3, y + Math.sin(t * 12 + y) * 1.2, -4.8, y * 1.6, 0.5, 1, 3);
      sEll(0.6, 0, 2.0 * (1 - 0.25 * c2), 2.6 * (1 - 0.2 * c2)); shape(p, s, 0.9);
      p.disc(0.6, 0, 0.6, DARK, 0.8 * s.alpha);
      return;
    }
    default: { // A barbed spine.
      const fl = Math.sin(t * 28) > 0 ? 1 : -1;
      sc(-6, 0, 3, 0, 0.7);
      sPoly(POD9_HEAD, 3);
      shape(p, s);
      strokeL(p, s, -5, 0, -7, 1.7 * fl, 0.6);
      strokeL(p, s, -3.6, 0, -5.4, -1.5 * fl, 0.6);
    }
  }
}
const POD9_HEAD = [4.6, 0, 1.8, -1.2, 1.8, 1.2];

/**
 * The trail of a pod behind it: ten points a step `step` apart astern, each
 * at the lateral offset `lat(j)` against the pod. The page samples the run
 * path; the map has the round's own place and its run share alone, so the
 * trail stands on the wave the run share states.
 */
export function drawPodTrail(
  design: number, p: MonsterPen, s: MonsterState, u: number, ph: number, step: number,
): void {
  const n = 10, t = s.t;
  const l0 = podLateral(design, u, ph);
  let ax = 0, ay = 0;
  for (let j = 1; j <= n; j++) {
    const uj = u - j * 0.03;
    if (uj < 0) break;
    const x = -j * step, y = podLateral(design, uj, ph) - l0, f = 1 - j / n;
    switch (design) {
      case 1:
        strokeL(p, s, ax, ay, x, y, 0.9, 0.35 * f);
        break;
      case 2:
        if (j % 2) p.ring(x, y + Math.sin(j * 2.1) * 1.2, 0.5 + j * 0.12, 0.5, LIGHT, 0.38 * f * s.alpha);
        break;
      case 3:
        if (j % 2 === 0) p.ring(x, y, 0.7 + j * 0.22, 0.5, LIGHT, 0.4 * f * s.alpha);
        break;
      default:
        p.disc(x, y + Math.sin(j * 1.4 + t * 14) * 1.7, 0.75 * f + 0.25, LIGHT, 0.55 * f * s.alpha);
    }
    ax = x; ay = y;
  }
}

/** The burst of a pod on a hull, `e` from 0 to 1 (`hit09` to `hit12`). */
export function drawPodBurst(design: number, p: MonsterPen, s: MonsterState, e: number): void {
  switch (design) {
    case 1: {
      if (e < 0.4) p.glow(0, 0, 6, 0.5 * (1 - e / 0.4) * s.alpha);
      for (let b = 0; b < 9; b++) {
        const a = b * TAU / 9, r = 2 + 11 * eout(e);
        p.disc(Math.cos(a) * r, Math.sin(a) * r, 1.1 * (1 - 0.4 * e), LIGHT, (1 - e) * s.alpha);
      }
      return;
    }
    case 2: {
      if (e < 0.55) {
        p.push(-1.6, 0, 0, 0.8); drawPod(2, p, s); p.pop();
        p.ring(0, 0, 2.5 + 3 * ((e * 4) % 1), 0.7, LIGHT, 0.5 * s.alpha);
      } else {
        const f = (e - 0.55) / 0.45;
        for (let b = 0; b < 7; b++) {
          const a = b * TAU / 7, r = 2 + 8 * eout(f);
          p.disc(Math.cos(a) * r, Math.sin(a) * r, 0.9, LIGHT, (1 - f) * s.alpha);
        }
        p.ring(0, 0, 3 + 8 * f, 0.8, LIGHT, 0.6 * (1 - f) * s.alpha);
      }
      return;
    }
    case 3: {
      const g = eout(e * 2.2);
      p.glow(0, 0, 6, 0.4 * (1 - e) * s.alpha);
      for (let b = 0; b < 10; b++) {
        const a = b * TAU / 10, ca = Math.cos(a), sa = Math.sin(a);
        let ax = ca * 2, ay = sa * 2;
        for (let i = 1; i <= 5; i++) {
          const r = 2 + 9 * g * i / 5, w = Math.sin(i * 1.8 + b) * 1.1 * i / 5;
          const bx = ca * r - sa * w, by = sa * r + ca * w;
          strokeL(p, s, ax, ay, bx, by, 0.55, 1 - e);
          ax = bx; ay = by;
        }
      }
      return;
    }
    default: {
      const g = eout(e * 3);
      p.glow(0, 0, 7, 0.35 * (1 - e) * s.alpha);
      for (let b = 0; b < 7; b++) {
        const a = b * TAU / 7 + 0.4, r1 = 2 + 6 * g;
        strokeL(p, s, Math.cos(a) * 2, Math.sin(a) * 2, Math.cos(a) * r1, Math.sin(a) * r1, 0.8, 1 - e);
        p.disc(Math.cos(a) * r1, Math.sin(a) * r1, 0.6, LIGHT, (1 - e) * s.alpha);
      }
    }
  }
}

/** How long a pod burst plays, in seconds (`0.16` of an attack of 5 s). */
export const POD_BURST_SEC = 0.8;
